import { useState, useCallback, useRef, useEffect } from 'react'

import axios from 'axios'

import useSentry from './useSentry'

function useApiGet({ initialLoading = false } = {}) {
  const [isLoading, setIsLoading] = useState(initialLoading)
  const [error, setError] = useState(null)
  const { captureException } = useSentry()
  const abortControllerRef = useRef(null)

  const getData = useCallback(({ path, params, onSuccess, onError }) => {
    // Abort any previous in-flight request to prevent race conditions
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    setError(null)

    const metaToken = document.getElementsByName('csrf-token')[0]
    const config = {
      headers: { 'X-CSRF-Token': metaToken?.getAttribute('content'), accept: 'application/json' },
      withCredentials: true,
      signal: abortControllerRef.current.signal,
    }

    // Add query parameters if provided
    if (params) {
      config.params = params
    }

    axios.get(path, config)
      .then(response => {
        setIsLoading(false)
        if (onSuccess) onSuccess(response)
      })
      .catch((internalError) => {
        // Silently ignore aborted requests - expected on navigation/unmount
        if (axios.isCancel(internalError) || internalError.code === 'ERR_CANCELED') {
          setIsLoading(false)
          return
        }

        setIsLoading(false)
        setError(internalError)
        captureException(internalError)

        if (onError) onError(internalError)
      })
  }, [captureException])

  // Cleanup: abort any pending request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    getData,
    isLoading,
    error,
  }
}

export default useApiGet
